import { Vendor, MSA, SOW, BillingEntry, Notification } from '../types';

// ─── Storage keys ─────────────────────────────────────────────────────────────
const KEYS = {
  vendors:       'vm_vendors',
  msas:          'vm_msas',
  sows:          'vm_sows',
  billing:       'vm_billing',
  notifications: 'vm_notifications',
  roleOverride:  'vm_role_override',
} as const;

// ─── Generic helpers ──────────────────────────────────────────────────────────
function load<T>(key: string): T[] {
  try {
    return JSON.parse(localStorage.getItem(key) || '[]') as T[];
  } catch {
    return [];
  }
}

function save<T>(key: string, items: T[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(items));
  } catch (e) {
    if (e instanceof DOMException && (e.name === 'QuotaExceededError' || e.code === 22)) {
      console.error(`[VendorMgmt] localStorage quota exceeded for "${key}". Remove uploaded documents to free space.`);
    }
    throw e;
  }
}

function uuid(): string {
  return crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function now(): string {
  return new Date().toISOString();
}

// ─── Vendors ──────────────────────────────────────────────────────────────────
export const vendorStore = {
  list(): Vendor[] {
    return load<Vendor>(KEYS.vendors);
  },

  listByCategory(category: string): Vendor[] {
    return load<Vendor>(KEYS.vendors).filter(v => v.categories.includes(category as never));
  },

  get(id: string): Vendor | undefined {
    return load<Vendor>(KEYS.vendors).find(v => v.id === id);
  },

  create(data: Omit<Vendor, 'id' | 'createdAt' | 'updatedAt' | 'totalSpend'>): Vendor {
    const all = load<Vendor>(KEYS.vendors);
    const vendor: Vendor = {
      ...data,
      id: uuid(),
      totalSpend: 0,
      createdAt: now(),
      updatedAt: now(),
    };
    all.push(vendor);
    save(KEYS.vendors, all);
    return vendor;
  },

  update(id: string, data: Partial<Vendor>): Vendor | undefined {
    const all = load<Vendor>(KEYS.vendors);
    const idx = all.findIndex(v => v.id === id);
    if (idx === -1) return undefined;
    all[idx] = { ...all[idx], ...data, id, updatedAt: now() };
    save(KEYS.vendors, all);
    return all[idx];
  },

  archive(id: string): void {
    vendorStore.update(id, { status: 'Archived' });
  },

  // Recomputes totalSpend from approved billing entries for this vendor.
  // Phase 2 TODO: apply rollupSpendToParent — if child has rollupSpendToParent=true,
  // add its totalSpend to the parent vendor's totalSpend as well.
  refreshSpend(vendorId: string): void {
    const entries = billingStore.listByVendor(vendorId).filter(e => e.authStatus === 'Approved');
    const total = entries.reduce((sum, e) => sum + e.amount, 0);
    vendorStore.update(vendorId, { totalSpend: total });
  },
};

// ─── MSAs ─────────────────────────────────────────────────────────────────────
export const msaStore = {
  list(): MSA[] {
    return load<MSA>(KEYS.msas);
  },

  listByVendor(vendorId: string): MSA[] {
    return load<MSA>(KEYS.msas).filter(m => m.vendorId === vendorId);
  },

  get(id: string): MSA | undefined {
    return load<MSA>(KEYS.msas).find(m => m.id === id);
  },

  create(data: Omit<MSA, 'id' | 'createdAt' | 'updatedAt' | 'authHistory'>): MSA {
    const all = load<MSA>(KEYS.msas);
    const msa: MSA = {
      ...data,
      id: uuid(),
      authHistory: [],
      createdAt: now(),
      updatedAt: now(),
    };
    all.push(msa);
    save(KEYS.msas, all);
    return msa;
  },

  update(id: string, data: Partial<MSA>): MSA | undefined {
    const all = load<MSA>(KEYS.msas);
    const idx = all.findIndex(m => m.id === id);
    if (idx === -1) return undefined;
    all[idx] = { ...all[idx], ...data, id, updatedAt: now() };
    save(KEYS.msas, all);
    return all[idx];
  },
};

// ─── SOWs ─────────────────────────────────────────────────────────────────────
export const sowStore = {
  list(): SOW[] {
    return load<SOW>(KEYS.sows);
  },

  listByVendor(vendorId: string): SOW[] {
    return load<SOW>(KEYS.sows).filter(s => s.vendorId === vendorId);
  },

  listByMSA(msaId: string): SOW[] {
    return load<SOW>(KEYS.sows).filter(s => s.msaId === msaId);
  },

  get(id: string): SOW | undefined {
    return load<SOW>(KEYS.sows).find(s => s.id === id);
  },

  create(data: Omit<SOW, 'id' | 'createdAt' | 'updatedAt' | 'authHistory'>): SOW {
    const all = load<SOW>(KEYS.sows);
    const sow: SOW = {
      ...data,
      id: uuid(),
      authHistory: [],
      createdAt: now(),
      updatedAt: now(),
    };
    all.push(sow);
    save(KEYS.sows, all);
    return sow;
  },

  update(id: string, data: Partial<SOW>): SOW | undefined {
    const all = load<SOW>(KEYS.sows);
    const idx = all.findIndex(s => s.id === id);
    if (idx === -1) return undefined;
    all[idx] = { ...all[idx], ...data, id, updatedAt: now() };
    save(KEYS.sows, all);
    return all[idx];
  },
};

// ─── Billing ──────────────────────────────────────────────────────────────────
export const billingStore = {
  list(): BillingEntry[] {
    return load<BillingEntry>(KEYS.billing);
  },

  listByVendor(vendorId: string): BillingEntry[] {
    return load<BillingEntry>(KEYS.billing).filter(e => e.vendorId === vendorId);
  },

  listByCategory(category: string): BillingEntry[] {
    const vendors = vendorStore.listByCategory(category).map(v => v.id);
    return load<BillingEntry>(KEYS.billing).filter(e => vendors.includes(e.vendorId));
  },

  get(id: string): BillingEntry | undefined {
    return load<BillingEntry>(KEYS.billing).find(e => e.id === id);
  },

  create(data: Omit<BillingEntry, 'id' | 'createdAt' | 'authHistory'>): BillingEntry {
    const all = load<BillingEntry>(KEYS.billing);
    const entry: BillingEntry = {
      ...data,
      id: uuid(),
      authHistory: [],
      createdAt: now(),
    };
    all.push(entry);
    save(KEYS.billing, all);
    return entry;
  },

  update(id: string, data: Partial<BillingEntry>): BillingEntry | undefined {
    const all = load<BillingEntry>(KEYS.billing);
    const idx = all.findIndex(e => e.id === id);
    if (idx === -1) return undefined;
    all[idx] = { ...all[idx], ...data, id };
    save(KEYS.billing, all);
    // Refresh parent vendor spend on approval status change
    if (data.authStatus) vendorStore.refreshSpend(all[idx].vendorId);
    return all[idx];
  },
};

// ─── Notifications ────────────────────────────────────────────────────────────
export const notificationStore = {
  list(): Notification[] {
    return load<Notification>(KEYS.notifications).sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  },

  unreadCount(): number {
    return load<Notification>(KEYS.notifications).filter(n => !n.read).length;
  },

  create(data: Omit<Notification, 'id' | 'createdAt' | 'read'>): Notification {
    const all = load<Notification>(KEYS.notifications);
    const n: Notification = { ...data, id: uuid(), read: false, createdAt: now() };
    all.push(n);
    save(KEYS.notifications, all);
    return n;
  },

  markRead(id: string): void {
    const all = load<Notification>(KEYS.notifications);
    const idx = all.findIndex(n => n.id === id);
    if (idx !== -1) { all[idx].read = true; save(KEYS.notifications, all); }
  },

  markAllRead(): void {
    const all = load<Notification>(KEYS.notifications).map(n => ({ ...n, read: true }));
    save(KEYS.notifications, all);
  },

  // Check expiration alerts — run on app load
  checkExpirations(vmEmail: string, dmEmail: string): void {
    const today = new Date();
    const thresholds = [90, 60, 30];
    const existing = load<Notification>(KEYS.notifications);

    const alreadyFired = (refId: string, days: number) =>
      existing.some(n => n.referenceId === refId && n.daysUntil === days);

    const makeMailto = (vendorName: string, contractLabel: string, expDate: string, days: number) => {
      const subject = encodeURIComponent(`[Contract Alert] ${vendorName} ${contractLabel} expiring in ${days} days`);
      const body = encodeURIComponent(
        `This is a contract expiration reminder.\n\nVendor: ${vendorName}\nContract: ${contractLabel}\nExpiration Date: ${expDate}\nDays Remaining: ${days}\n\nPlease take appropriate action to renew or wind down this contract.\n\nThis alert was generated by the Christmas Lumber Vendor Management system.`
      );
      return `mailto:${vmEmail},${dmEmail}?subject=${subject}&body=${body}`;
    };

    const msas = load<MSA>(KEYS.msas);
    const sows = load<SOW>(KEYS.sows);
    const vendors = load<Vendor>(KEYS.vendors);
    const vendorMap = new Map(vendors.map(v => [v.id, v.name]));

    for (const msa of msas) {
      if (!msa.expirationDate || msa.authStatus !== 'Executed') continue;
      const exp = new Date(msa.expirationDate);
      const daysUntil = Math.floor((exp.getTime() - today.getTime()) / 86_400_000);
      for (const threshold of thresholds) {
        if (daysUntil <= threshold && daysUntil >= threshold - 2 && !alreadyFired(msa.id, threshold)) {
          const vendorName = vendorMap.get(msa.vendorId) ?? 'Unknown Vendor';
          notificationStore.create({
            type: 'ExpirationAlert',
            referenceId: msa.id,
            referenceType: 'MSA',
            vendorId: msa.vendorId,
            message: `MSA for ${vendorName} expires in ${daysUntil} days (${msa.expirationDate})`,
            daysUntil: threshold, // store bucket (90/60/30) as dedup key; exact days shown in message
            mailtoHref: makeMailto(vendorName, 'MSA', msa.expirationDate, daysUntil),
          });
        }
      }
    }

    for (const sow of sows) {
      if (!sow.endDate || sow.authStatus !== 'Executed') continue;
      const exp = new Date(sow.endDate);
      const daysUntil = Math.floor((exp.getTime() - today.getTime()) / 86_400_000);
      for (const threshold of thresholds) {
        if (daysUntil <= threshold && daysUntil >= threshold - 2 && !alreadyFired(sow.id, threshold)) {
          const vendorName = vendorMap.get(sow.vendorId) ?? 'Unknown Vendor';
          notificationStore.create({
            type: 'ExpirationAlert',
            referenceId: sow.id,
            referenceType: 'SOW',
            vendorId: sow.vendorId,
            message: `SOW "${sow.name}" for ${vendorName} expires in ${daysUntil} days (${sow.endDate})`,
            daysUntil: threshold, // store bucket (90/60/30) as dedup key; exact days shown in message
            mailtoHref: makeMailto(vendorName, `SOW ${sow.sowNumber}`, sow.endDate, daysUntil),
          });
        }
      }
    }
  },
};
